import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Star, ShieldCheck, Ruler, PackageCheck, SearchCheck, Truck, Lock, Undo2, ShoppingBag } from "lucide-react";
import { motion, AnimatePresence, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { Ticker } from "../components/Ticker";
import { toast } from "sonner";
import api, { formatPrice, CONFIG, fetchConfig, fetchProductsCached } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { DROP_IMGS } from "../lib/dropImages";
import { MountainWordmark, RidgeLine } from "../components/Logo";
import { SEO } from "../components/SEO";
import { trackAddToCart } from "../lib/googleAds";
import { trackEvent } from "../lib/analytics";
import { flyToCart } from "../lib/flyToCart";

const EASE = [0.16, 1, 0.3, 1];

/* Real Zejzen TNF drop photos (user-provided) */
const SLIDES = [
  {
    img: "/tnf/tnf-jackets.webp",
    tag: "THE NORTH FACE", label: "JACKETS",
  },
  {
    img: "/tnf/denali-fleece.webp",
    tag: "THE NORTH FACE", label: "DENALI FLEECE",
  },
  {
    img: "/api/files/zejzen/drops/patagonia-fleece.webp",
    tag: "PATAGONIA", label: "FLEECE",
  },
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const V = {
  bg: "var(--zjz-bg)", surface: "var(--zjz-surface)", border: "var(--zjz-border)",
  text: "var(--zjz-text)", muted: "var(--zjz-muted)", accent: "var(--zjz-accent)",
  bright: "var(--zjz-accent-bright)", navy: "var(--zjz-navy)", navyText: "var(--zjz-navy-text)",
  sand: "var(--zjz-sand)", olive: "var(--zjz-olive)", cream: "var(--zjz-cream)",
};

const mono = "font-badge text-[10px] tracking-[0.2em]";
const h2cls = "font-['Space_Grotesk'] text-3xl sm:text-5xl font-bold uppercase tracking-tight leading-[1.0]";

/* cinematic single-card deck: crossfade + scale/rotate scene changes, mouse 3D tilt */
function HeroDeck({ t, slides = SLIDES, chip }) {
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const tiltX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 140, damping: 16 });
  const tiltY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 140, damping: 16 });
  // floating chips parallax at three different depths (true layered 3D feel)
  const c1x = useSpring(useTransform(mx, [-0.5, 0.5], [-16, 16]), { stiffness: 90, damping: 14 });
  const c1y = useSpring(useTransform(my, [-0.5, 0.5], [-10, 10]), { stiffness: 90, damping: 14 });
  const c2x = useSpring(useTransform(mx, [-0.5, 0.5], [22, -22]), { stiffness: 70, damping: 14 });
  const c2y = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 70, damping: 14 });
  const c3x = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 16 });
  const c3y = useSpring(useTransform(my, [-0.5, 0.5], [-6, 6]), { stiffness: 110, damping: 16 });
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const rm = window.matchMedia("(prefers-reduced-motion: reduce)");
    const up = () => setReduced(rm.matches);
    up();
    rm.addEventListener("change", up);
    return () => rm.removeEventListener("change", up);
  }, []);

  useEffect(() => {
    if (reduced) return;
    const tm = setInterval(() => setIdx((i) => (i + 1) % slides.length), 4500);
    return () => clearInterval(tm);
  }, [reduced, slides.length]);

  useEffect(() => { setIdx((i) => (slides.length ? i % slides.length : 0)); }, [slides.length]);

  const onMove = (e) => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const s = slides[idx % slides.length] || SLIDES[0];

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative w-full aspect-[4/5] max-h-[540px] select-none"
      style={{ perspective: "1400px" }}
      data-testid="hero-deck"
    >
      {/* breathing themed light wash behind the card (static — infinite loops were the scroll jank) */}
      <div className="absolute -inset-10 pointer-events-none"
        style={{ background: `radial-gradient(ellipse 62% 55% at 50% 42%, ${V.accent}33 0%, transparent 70%)` }} />
      <div className="h-full">
        <AnimatePresence mode="popLayout">
          <motion.div
            key={idx}
            className="absolute inset-0 overflow-hidden rounded-[28px] cursor-pointer"
            style={{
              border: `1px solid ${V.border}`,
              boxShadow: "0 50px 100px -40px rgba(27,30,26,0.5)",
              rotateX: tiltX, rotateY: tiltY, transformStyle: "preserve-3d",
            }}
            initial={{ opacity: 0, scale: 0.88, rotateY: -16, y: 34, filter: "brightness(0.85)" }}
            animate={{ opacity: 1, scale: 1, rotateY: 0, y: 0, filter: "brightness(1)" }}
            exit={{ opacity: 0, scale: 1.05, rotateY: 12, y: -26, filter: "brightness(1.1)" }}
            transition={{ duration: 0.65, ease: EASE }}
            data-testid={`hero-slide-${idx}`}
          >
            <motion.img
              src={s.img}
              alt={`${s.tag} ${s.label}`}
              className="w-full h-full object-cover"
              draggable="false"
              loading={idx === 0 ? "eager" : "lazy"}
              decoding="async"
            />
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(20,24,20,0.55) 0%, transparent 45%)" }} />
            {s.to && <Link to={s.to} className="absolute inset-0 z-10" aria-label={`${s.tag} ${s.label}`} data-testid={`hero-slide-link-${idx}`} />}
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-2 pointer-events-none z-[11]">
              <div>
                <p className="font-badge text-[9px] text-white/80">{s.tag}</p>
                <p className="font-['Space_Grotesk'] text-xl font-bold uppercase text-white leading-tight">{s.label}</p>
              </div>
              <span className="font-badge text-[8px] px-2.5 py-1 text-white rounded-full" style={{ background: chip?.bg || "rgba(30,40,54,0.85)", backdropFilter: "blur(4px)" }} data-testid={`hero-slide-chip-${idx}`}>
                {chip?.text || t("statusComingSoon").toUpperCase()}
              </span>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      {/* floating identity chips at three parallax depths */}
      <motion.span style={{ x: c1x, y: c1y }} className="absolute -top-5 -left-2 sm:-left-6 z-30 hidden sm:inline-block">
        <span className="zjz-chip inline-block font-badge text-[9px] px-3 py-1.5 rounded-full" style={{ background: "var(--zjz-surface)", border: "1px solid var(--zjz-border)", boxShadow: "0 8px 20px -10px rgba(27,30,26,0.35)", "--chip-rot": "-4deg", animationDelay: "0s" }} data-testid="hero-chip-preowned">{t("tw1")}</span>
      </motion.span>
      <motion.span style={{ x: c2x, y: c2y }} className="absolute bottom-24 -right-2 sm:-right-8 z-30 hidden sm:inline-block">
        <span className="zjz-chip inline-block font-badge text-[9px] px-3 py-1.5 rounded-full" style={{ background: "var(--zjz-navy)", color: "var(--zjz-navy-text)", boxShadow: "0 8px 20px -10px rgba(27,30,26,0.4)", "--chip-rot": "3deg", animationDelay: "1.2s" }} data-testid="hero-chip-oneofone">{t("tw2")}</span>
      </motion.span>
      <motion.span style={{ x: c3x, y: c3y }} className="absolute -bottom-4 left-8 sm:left-10 z-30 hidden sm:inline-block">
        <span className="zjz-chip inline-block font-badge text-[9px] px-3 py-1.5 rounded-full" style={{ background: "var(--zjz-accent)", color: "#fff", boxShadow: "0 8px 20px -10px rgba(27,30,26,0.4)", "--chip-rot": "-2deg", animationDelay: "2.3s" }} data-testid="hero-chip-checked">{t("tw4")}</span>
      </motion.span>

      {/* catalog-style index + progress (auto-rotating, non-interactive) */}
      <div className="absolute -bottom-8 left-[6%] right-[6%] flex items-center justify-between z-20" data-testid="hero-deck-dots">
        <div className="flex gap-2">
          {slides.map((_, i) => (
            <span
              key={i}
              data-testid={`hero-dot-${i}`}
              className="h-[3px] transition-all duration-500 rounded-full"
              style={{ width: i === idx ? 28 : 12, background: i === idx ? V.accent : V.border }}
            />
          ))}
        </div>
        <span className="font-badge text-[9px] tracking-[0.25em]" style={{ color: V.muted }}>
          {String(idx + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
}

/* rotating identity word under the headline */
function TickerWord() {
  const { t } = useLang();
  const words = [t("tw1"), t("tw2"), t("tw3"), t("tw4")];
  const [i, setI] = useState(0);
  useEffect(() => {
    const tm = setInterval(() => setI((v) => (v + 1) % words.length), 2400);
    return () => clearInterval(tm);
  }, [words.length]);
  return (
    <span className="inline-block relative h-[1.1em] overflow-hidden align-bottom" style={{ minWidth: "12ch" }}>
      <AnimatePresence mode="wait">
        <motion.span
          key={i}
          className="inline-block"
          style={{ color: V.accent }}
          initial={{ y: "110%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "-110%", opacity: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
          data-testid="hero-ticker-word"
        >
          {words[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

export default function Home() {
  const { t, catName, lang } = useLang();
  const { scrollY } = useScroll();
  const heroDeckY = useTransform(scrollY, [0, 700], [0, 70]); // gentle parallax on the hero deck
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState(null);
  const [site, setSite] = useState(null);
  const [nextDrop, setNextDrop] = useState(null);
  const [nlEmail, setNlEmail] = useState("");
  const [now, setNow] = useState(Date.now());
  const [faqs, setFaqs] = useState([]);
  const [faqOpen, setFaqOpen] = useState(null);

  useEffect(() => {
    fetchConfig().then(() => { setSite(CONFIG.site); setNextDrop(CONFIG.next_drop || null); });
    fetchProductsCached().then(setProducts).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!site?.drop_date) return;
    const tm = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tm);
  }, [site?.drop_date]);

  const realProducts = useMemo(() => products.filter((p) => !p.is_demo), [products]);
  // categories follow the catalog itself — nothing hardcoded
  const shopCats = useMemo(() => [...new Set(realProducts.map((p) => p.category).filter(Boolean))], [realProducts]);
  const featured = useMemo(() => {
    if (!realProducts.length) return [];
    if (site?.featured_ids?.length) {
      const picked = site.featured_ids.map((id) => realProducts.find((p) => (p.id || p._id) === id)).filter(Boolean);
      if (picked.length) return picked.slice(0, 8);
    }
    return realProducts.slice(0, 8);
  }, [realProducts, site]);

  const dropName = nextDrop?.name || site?.theme?.name || "STORM SHELLS";
  const dropBrands = site?.theme?.brands || "";
  const dropDesc = site?.drop_desc || "";
  const dropStatus = site?.drop_status || "coming_soon";
  const dropTs = nextDrop?.release_at ? new Date(nextDrop.release_at).getTime() : (site?.drop_date ? new Date(site.drop_date).getTime() : null);
  const dropLeft = dropTs ? Math.max(0, dropTs - now) : null;
  const cd = dropLeft != null ? {
    d: Math.floor(dropLeft / 86400000), h: Math.floor((dropLeft % 86400000) / 3600000),
    m: Math.floor((dropLeft % 3600000) / 60000), s: Math.floor((dropLeft % 60000) / 1000),
  } : null;

  const subscribe = async (email) => {
    if (!email.trim()) return;
    try {
      const r = await api.post("/subscribe", { email });
      toast.success(r.data?.pending ? t("subscribedPending") : t("subscribed"));
    } catch {
      toast.error(t("contactErr"));
    }
  };

  const statusLabel = { coming_soon: t("statusComingSoon"), live: t("statusLive"), sold_out: t("statusSoldOut"), archived: t("statusArchived") }[dropStatus] || dropStatus;
  const isLive = dropStatus === "live";
  const statusBadge = isLive ? t("statusLiveNow") : statusLabel;
  // next themed drop after the active one — drives the "coming soon" fold
  const nextThemeDrop = (site?.drops || []).find((d) => d.key !== site?.active_drop);
  const csName = nextThemeDrop?.name || dropName;
  // hero deck shows the live drop's real pieces (real photos first, never AI previews)
  const heroSlides = useMemo(() => {
    const pool = realProducts.filter((p) => !site?.active_drop || p.drop === site.active_drop);
    const avail = pool.filter((p) => p.status === "active" && p.images?.length);
    const picks = (avail.length ? avail : pool.filter((p) => p.images?.length)).slice(0, 4);
    return picks.map((p) => ({
      img: (p.images || []).find((i) => !i.includes("/model/")) || p.images[0],
      tag: (p.brand || dropName).trim(),
      label: p.name,
      to: `/item/${p.id || p._id}`,
    }));
  }, [realProducts, site, dropName]);

  return (
    <div data-testid="home-page" style={{ background: V.bg, color: V.text }} className="transition-colors duration-500">
      <SEO
        title="ZEJZEN — STORM SHELLS · JETZT LIVE | The North Face Jackets Archive"
        description="Storm Shells ist live: kuratierte The North Face Jackets — pre-owned, einzeln geprüft, vermessen, one of one. Versand aus Deutschland. Weg ist weg."
        path="/"
        image="https://zejzen.com/api/files/zejzen/products/TNF05/117dc635834c_1600.webp"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "ZejZen",
          url: "https://zejzen.com",
          logo: "https://zejzen.com/og.png",
          email: "info@zejzen.com",
          address: { "@type": "PostalAddress", addressCountry: "DE" },
        }}
      />

      {site?.drop_banner && (
        <div className="py-2 overflow-hidden" style={{ background: V.accent }} data-testid="drop-banner">
          <Ticker duration={26} testid="drop-banner-ticker">
            {Array.from({ length: 6 }).map((_, i) => (
              <span key={i} className="font-badge text-[10px] text-white mx-8 whitespace-nowrap">{site.drop_banner}</span>
            ))}
          </Ticker>
        </div>
      )}

      {/* 1 — HERO: kinetic type + 2.5D slide deck (no single dominating photo) */}
      <section className="grid grid-cols-1 lg:grid-cols-12 items-center overflow-hidden" style={{ borderBottom: `1px solid ${V.border}` }} data-testid="hero-section">
        <div className="lg:col-span-7 px-6 sm:px-12 py-14 sm:py-20 lg:py-24 flex flex-col justify-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
            <span className="inline-flex items-center gap-2 font-badge text-[9px] px-3 py-1.5" style={{ border: `1px solid ${V.accent}`, color: V.accent }} data-testid="hero-drop-badge">
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: V.accent }} />
              {nextDrop ? `${nextDrop.name} · ${t("statusComingSoon").toUpperCase()}` : `${dropName} · ${String(statusBadge).toUpperCase()}`}
              {nextDrop && cd && (
                <span className="tabular-nums" data-testid="hero-countdown">{cd.d}T {String(cd.h).padStart(2, "0")}STD {String(cd.m).padStart(2, "0")}MIN {String(cd.s).padStart(2, "0")}SEK</span>
              )}
            </span>
          </motion.div>
          <h1 className="font-['Space_Grotesk'] text-5xl sm:text-7xl lg:text-8xl font-bold uppercase leading-[0.92] tracking-tighter mt-8" data-testid="hero-title">
            <motion.span className="block" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: EASE }}>
              {site?.hero_title ? site.hero_title : t("heroTitle1")}
            </motion.span>
            {!site?.hero_title && (
              <motion.span className="block outline-tech-w" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2, ease: EASE }}>
                {t("heroTitle2")}
              </motion.span>
            )}
          </h1>
          <motion.p
            className="mt-5 font-badge text-[11px] tracking-[0.25em]"
            style={{ color: V.muted }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.3 }}
            data-testid="hero-ticker"
          >
            <TickerWord />
          </motion.p>
          <motion.p
            className="mt-4 max-w-md text-sm sm:text-base leading-relaxed"
            style={{ color: V.muted }}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.36, ease: EASE }}
            data-testid="hero-sub"
          >
            {site?.hero_sub || dropDesc || t("heroSub")}
          </motion.p>
          <motion.div
            className="mt-9 flex flex-wrap gap-3"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.44, ease: EASE }}
          >
            <Link
              to={isLive && site?.active_drop ? `/drops/${site.active_drop}` : "/shop"}
              data-testid="hero-explore-button"
              className="inline-flex items-center gap-2 font-badge text-xs px-8 py-4 text-white transition-opacity hover:opacity-90"
              style={{ background: V.accent }}
            >
              {t("exploreDrop")} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <a
              href="#newsletter"
              data-testid="hero-join-button"
              className="inline-flex items-center gap-2 font-badge text-xs px-8 py-4 transition-colors"
              style={{ border: `1px solid ${V.text}`, color: V.text }}
            >
              {t("joinDrop")}
            </a>
          </motion.div>
          {reviews && (
            <motion.div
              className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.55 }}
              data-testid="hero-trust-row"
            >
              <span className={mono} style={{ color: V.muted }}>★ {reviews.rating_avg} · {reviews.count} {t("verifiedPurchase")}</span>
              <span className={mono} style={{ color: V.muted }}>{t("shipsIn")}</span>
              <span className={mono} style={{ color: V.muted }}>{t("dispatchFrom")}</span>
            </motion.div>
          )}
        </div>
        <div className="lg:col-span-5 px-6 sm:px-12 pb-16 lg:py-20 flex items-center justify-center relative" style={{ background: V.sand }}>
          {/* spinning circular badge */}
          <div className="absolute top-6 right-6 sm:right-10 z-30 hidden sm:block" data-testid="hero-ring-badge">
            <svg viewBox="0 0 120 120" className="w-24 h-24 zjz-spin">
              <defs><path id="ringPath" d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0" /></defs>
              <circle cx="60" cy="60" r="58" fill="var(--zjz-surface)" stroke="var(--zjz-border)" />
              <text fontFamily="'JetBrains Mono', monospace" fontSize="10.5" letterSpacing="2.5" fill="var(--zjz-accent)">
                <textPath href="#ringPath">SECOND-HAND ARCHIVE · ONE OF ONE · EST. GERMANY ·</textPath>
              </text>
              <path d="M42 78 L52 58 L60 70 L68 54 L78 78 Z" fill="none" stroke="var(--zjz-text)" strokeWidth="2.5" strokeLinejoin="round" />
            </svg>
          </div>
          <motion.div
            className="w-full max-w-[520px]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
          >
            <motion.div style={{ y: heroDeckY }}>
              <HeroDeck t={t} slides={heroSlides.length ? heroSlides : SLIDES} chip={isLive ? { text: t("statusLiveNow").toUpperCase(), bg: V.accent } : undefined} />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 1.5 — SECOND-HAND IDENTITY BAND (unmistakable) */}
      <section className="px-6 sm:px-12 py-14 sm:py-20" style={{ background: V.text, color: V.bg }} data-testid="identity-section">
        <Reveal className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <div>
            <MountainWordmark text="ZEJZEN" className="font-['Space_Grotesk'] text-lg font-extrabold tracking-[0.3em] mb-8 opacity-80" />
            <h2 className="font-['Space_Grotesk'] text-4xl sm:text-6xl font-bold uppercase tracking-tighter leading-[0.95]" data-testid="identity-title">
              {t("identityBig")}
            </h2>
          </div>
          <p className="max-w-md text-base sm:text-lg leading-relaxed" style={{ color: "rgba(250,247,240,0.78)" }} data-testid="identity-sub">
            {t("identitySub")}
          </p>
        </Reveal>
        <div className="mt-10 overflow-hidden">
          <Ticker duration={30} testid="identity-ticker">
            {Array.from({ length: 6 }).map((_, i) => (
              <span key={i} className="font-badge text-xs sm:text-sm mx-8 whitespace-nowrap" style={{ color: "rgba(250,247,240,0.65)" }} data-testid={`identity-tick-${i}`}>
                {t("tw1")} · {t("tw2")} · {t("tw3")} · {t("tw4")} ·
              </span>
            ))}
          </Ticker>
        </div>
      </section>

      {/* 3 — FEATURED (real pieces of the live drop) */}
      {featured.length > 0 && (
        <section id="featured" className="px-6 sm:px-12 py-16 sm:py-24" style={{ borderBottom: `1px solid ${V.border}` }} data-testid="featured-section">
          <Reveal className="flex items-end justify-between gap-4 mb-10">
            <div>
              <p className={mono} style={{ color: V.accent }}>{t("featuredKicker")}</p>
              <h2 className={`${h2cls} mt-3`}>{t("featuredTitle")}</h2>
            </div>
            <Link to="/shop" data-testid="featured-view-all" className="hidden sm:inline-flex items-center gap-2 font-badge text-[10px] hover:opacity-70 transition-opacity" style={{ color: V.text }}>
              {t("viewAll")} <ArrowRight size={12} strokeWidth={1.5} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {featured.map((p, i) => <PieceCard key={p.id || p._id} p={p} t={t} i={i} />)}
          </div>
        </section>
      )}

      {/* categories — derived from the actual catalog; grows as products are added */}
      {shopCats.length > 0 && (
      <section id="categories" className="px-6 sm:px-12 py-16 sm:py-20" style={{ borderBottom: `1px solid ${V.border}`, background: V.olive }} data-testid="categories-section">
        <Reveal className="mb-10 flex items-end justify-between gap-4">
          <p className={mono} style={{ color: V.accent }}>ZEJZEN / {t("categories").toUpperCase()}</p>
          <MountainWordmark text="ZEJZEN" className="hidden sm:inline-flex font-['Space_Grotesk'] text-sm font-extrabold tracking-[0.3em]" />
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:h-[320px]" data-testid="category-accordion">
            {shopCats.map((c, i) => {
              const catItems = products.filter((p) => p.category === c);
              const catImg = catItems[0]?.images?.[0];
              const catBrands = [...new Set(catItems.map((p) => p.brand).filter((b) => b && b !== "ZEJZEN PREVIEW"))];
              return (
                <Link
                  key={c}
                  to={`/shop?cat=${c}`}
                  data-testid={`home-category-${c}`}
                  className="group relative flex-1 hover:flex-[2.2] transition-all duration-500 ease-out overflow-hidden rounded-2xl px-6 py-8 flex flex-col justify-between min-h-[180px]"
                  style={{ border: `1px solid ${V.border}`, background: V.surface }}
                >
                  {catImg && (
                    <img
                      src={catImg}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-[0.14] transition-opacity duration-500 pointer-events-none"
                    />
                  )}
                  <div className="flex items-start justify-between relative">
                    <span className={mono} style={{ color: V.muted }}>{String(i + 1).padStart(2, "0")}</span>
                    <ArrowRight size={16} strokeWidth={1.5} style={{ color: V.accent }} className="transition-transform duration-300 group-hover:translate-x-1.5 group-hover:-translate-y-1.5" />
                  </div>
                  <div className="relative">
                    <p className="font-['Space_Grotesk'] text-2xl lg:text-3xl font-bold uppercase" style={{ color: V.text }}>{catName(c)}</p>
                    <p className="font-badge text-[9px] mt-2" style={{ color: V.muted }} data-testid={`home-category-count-${c}`}>
                      {catItems.length} {t("tw2").toUpperCase()} {catItems.length === 1 ? "PIECE" : "PIECES"}
                    </p>
                    {catBrands.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {catBrands.slice(0, 3).map((b) => (
                          <span key={b} className="font-badge text-[8px] px-2 py-1 rounded-full" style={{ border: `1px solid ${V.border}`, color: V.muted }}>{b}</span>
                        ))}
                      </div>
                    )}
                    <p className="font-badge text-[9px] mt-3 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300" style={{ color: V.accent }}>
                      {t("catExplore").toUpperCase()} →
                    </p>
                  </div>
                  <RidgeLine fill={V.olive} className="absolute bottom-0 left-0 w-full h-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                </Link>
              );
            })}
          </div>
          {/* drop info card fills the fold */}
          <div className="rounded-2xl px-7 py-8 flex flex-col justify-between gap-6 holo-border" style={{ background: V.navy, color: V.navyText }} data-testid="category-drop-card">
            <div>
              <p className="font-badge text-[9px] tracking-[0.3em]" style={{ color: "#9DB89A" }}>{dropName}</p>
              <p className="font-['Space_Grotesk'] text-xl font-bold uppercase mt-3 leading-snug">{t("identityBig")}</p>
              <p className="text-xs leading-relaxed mt-3" style={{ color: "rgba(232,230,223,0.65)" }}>{dropDesc || t("heroSub")}</p>
            </div>
            {isLive && site?.active_drop ? (
              <Link
                to={`/drops/${site.active_drop}`}
                data-testid="category-drop-cta"
                className="inline-flex items-center gap-2 font-badge text-[10px] px-5 py-3 self-start transition-opacity hover:opacity-85"
                style={{ background: "#5B8C5A", color: "#10180F" }}
              >
                {t("zumDrop").toUpperCase()} <ArrowRight size={12} strokeWidth={2} />
              </Link>
            ) : (
              <a
                href="#coming-soon"
                data-testid="category-drop-cta"
                className="inline-flex items-center gap-2 font-badge text-[10px] px-5 py-3 self-start transition-opacity hover:opacity-85"
                style={{ background: "#5B8C5A", color: "#10180F" }}
              >
                {t("comingSoon").toUpperCase()} <ArrowRight size={12} strokeWidth={2} />
              </a>
            )}
          </div>
        </div>
      </section>
      )}

      {/* 4 — COMING SOON: deep-navy campaign fold, curiosity-first */}
      <section id="coming-soon" className="relative overflow-hidden" style={{ background: V.navy, color: V.navyText }} data-testid="coming-soon-section">
        <RidgeLine fill={V.olive} className="w-full h-10 block -mb-px" />
        <div className="py-3" style={{ borderBottom: "1px solid rgba(232,230,223,0.15)" }}>
          <Ticker duration={24} testid="cs-ticker">
            {Array.from({ length: 8 }).map((_, i) => (
              <span key={i} className="font-badge text-[10px] mx-8 whitespace-nowrap" style={{ color: "rgba(232,230,223,0.65)" }} data-testid={`cs-marquee-${i}`}>
                {csName} · {t("statusComingSoon").toUpperCase()} · {t("oneOfOnePieces").toUpperCase()}
              </span>
            ))}
          </Ticker>
        </div>
        <div className="px-6 sm:px-12 py-16 sm:py-24">
          {/* giant animated campaign type */}
          <div className="mb-12">
            <motion.p
              className="font-badge text-[11px] tracking-[0.3em]"
              style={{ color: "#9DB89A" }}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              {t("nextDropPreview")}
            </motion.p>
            <h2 className="font-['Space_Grotesk'] font-bold uppercase leading-[0.9] tracking-tighter mt-4" data-testid="coming-soon-big">
              <motion.span
                className="block text-5xl sm:text-7xl lg:text-8xl"
                style={{ color: V.navyText }}
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: EASE }}
              >
                {csName}
              </motion.span>
              <motion.span
                className="block text-6xl sm:text-8xl lg:text-9xl"
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.12, ease: EASE }}
              >
                <span className="cs-shimmer">{t("comingSoon").toUpperCase()}</span>
              </motion.span>
            </h2>
            {cd && (
              <div className="flex gap-3 mt-8" data-testid="coming-soon-countdown">
                {[["d", cd.d], ["h", cd.h], ["m", cd.m], ["s", cd.s]].map(([u, val]) => (
                  <div key={u} className="px-4 py-3 text-center rounded-xl" style={{ border: "1px solid rgba(232,230,223,0.25)", background: "rgba(255,255,255,0.04)" }}>
                    <p className="font-['Space_Grotesk'] text-2xl font-bold" style={{ color: V.navyText }}>{String(val).padStart(2, "0")}</p>
                    <p className="font-badge text-[8px] mt-1" style={{ color: "rgba(232,230,223,0.55)" }}>{t(`cd_${u}`)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* DROP ROADMAP — each drop card tinted in its own theme */}
          <div className="max-w-4xl" data-testid="drops-roadmap">
            {(site?.drops || []).map((d, i) => {
              const isActive = d.key === site?.active_drop;
              const tags = [t("dropTag1"), t("dropTag2"), t("dropTag3"), t("dropTag4")];
              const status = isActive ? statusLabel : (i === 3 ? t("statusTba") : t("statusComingSoon"));
              return (
                <Reveal key={d.key} delay={i * 0.07}>
                  <Link
                    to={`/drops/${d.key}`}
                    className="group flex flex-col sm:flex-row sm:items-center gap-4 px-6 py-7 transition-all duration-300 hover:-translate-y-1"
                    style={{
                      background: d.bg, border: `1px solid ${d.border}`, borderLeft: `6px solid ${d.accent}`,
                      marginTop: i > 0 ? 12 : 0, borderRadius: 4,
                      boxShadow: isActive ? "0 24px 50px -30px rgba(0,0,0,0.55)" : "none",
                    }}
                    data-testid={`drop-card-${i}`}
                  >
                    <span className="font-badge text-[11px] tracking-[0.2em] shrink-0" style={{ color: d.muted }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-badge text-[9px] tracking-[0.2em]" style={{ color: d.muted }}>{d.brands}</p>
                      <p className="font-['Space_Grotesk'] text-lg sm:text-xl font-bold uppercase mt-1.5 leading-snug" style={{ color: d.text }}>
                        {(d.name.split("— ")[1] || d.name)}
                      </p>
                      <p className="text-xs mt-2" style={{ color: d.muted }}>{tags[i]}</p>
                      {isActive && isLive && (
                        <p className="font-badge text-[10px] mt-2.5 tracking-[0.15em]" style={{ color: d.accent }} data-testid="drop-live-cta">{t("zumDrop").toUpperCase()} →</p>
                      )}
                    </div>
                    {DROP_IMGS[d.key] && (
                      <img
                        src={DROP_IMGS[d.key]}
                        alt={d.name}
                        className="drop-thumb w-20 h-24 sm:w-24 sm:h-28 object-cover rounded-md self-start sm:self-center"
                        style={{ border: `1px solid ${d.border}` }}
                        data-testid={`drop-thumb-${i}`}
                      />
                    )}
                    <span
                      className="font-badge text-[9px] px-3.5 py-2 self-start sm:self-center whitespace-nowrap"
                      style={{
                        background: isActive ? d.accent : "transparent",
                        color: isActive ? d.bg : d.muted,
                        border: `1px solid ${isActive ? d.accent : d.border}`,
                      }}
                      data-testid={`drop-status-${i}`}
                    >
                      {String(status).toUpperCase()}
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl">
            <input
              data-testid="coming-soon-email-input"
              aria-label="E-Mail für Drop-Benachrichtigung"
              value={nlEmail}
              onChange={(e) => setNlEmail(e.target.value)}
              placeholder={t("subscribePh")}
              className="flex-1 px-4 py-3.5 text-sm focus:outline-none placeholder:text-[rgba(232,230,223,0.4)]"
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(232,230,223,0.25)", color: V.navyText }}
            />
            <button
              onClick={() => { subscribe(nlEmail); setNlEmail(""); }}
              data-testid="coming-soon-notify-button"
              className="font-badge text-xs px-6 py-3.5 transition-opacity hover:opacity-90"
              style={{ background: "#5B8C5A", color: "#10180F" }}
            >
              {t("joinList")}
            </button>
          </Reveal>
          <p className="mt-3 max-w-xl font-badge text-[9px] text-left" style={{ color: "rgba(232,230,223,0.5)" }} data-testid="cs-privacy-note">
            {t("subscribePrivacy")}
          </p>
        </div>
      </section>

      {/* 5 — ABOUT / HOW IT WORKS */}
      <section id="how" className="px-6 sm:px-12 py-16 sm:py-24" style={{ borderBottom: `1px solid ${V.border}` }} data-testid="process-section">
        <Reveal className="mb-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <p className={mono} style={{ color: V.accent }}>{t("aboutKicker")}</p>
            <h2 className={`${h2cls} mt-3`} data-testid="about-title">{t("aboutHomeTitle")}</h2>
            <MountainWordmark text="ZEJZEN" className="font-['Space_Grotesk'] text-base font-extrabold tracking-[0.3em] mt-6" />
          </div>
          <div className="lg:col-span-7 flex flex-col justify-end gap-4 max-w-2xl">
            <p className="text-sm sm:text-base leading-relaxed" style={{ color: V.text }} data-testid="about-p1">{t("aboutP1")}</p>
            <p className="text-sm leading-relaxed" style={{ color: V.muted }} data-testid="about-p2">{t("aboutP2")}</p>
            <p className="text-sm leading-relaxed" style={{ color: V.muted }} data-testid="about-p3">{t("aboutP3")}</p>
          </div>
        </Reveal>
        </section>

      {/* 6 — TRUST (real data only) */}
      <section className="py-16 sm:py-24" style={{ borderBottom: `1px solid ${V.border}`, background: V.cream }} data-testid="reviews-section">
        <div className="px-6 sm:px-12 mb-10 flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className={mono} style={{ color: V.accent }}>{t("trustKicker")}</p>
            <h2 className={`${h2cls} mt-3`}>{t("trustTitle")}</h2>
            <p className="font-badge text-[10px] mt-3" style={{ color: V.muted }} data-testid="reviews-source-note">{t("reviewsSourceNote")}</p>
          </Reveal>
          {reviews && (
            <Reveal className="flex gap-8" delay={0.1}>
              <div><p className="font-['Space_Grotesk'] text-3xl font-bold" style={{ color: V.text }} data-testid="trust-rating">{reviews.rating_avg}/5</p><p className={mono} style={{ color: V.muted }}>{t("avgRating")}</p></div>
              <div>
                <p className="font-['Space_Grotesk'] text-3xl font-bold" style={{ color: V.text }} data-testid="trust-count">{reviews.count}</p>
                <p className={mono} style={{ color: V.muted }}>{t("verifiedReviews")}</p>
                <a
                  href={reviews.profile_url || "https://www.vinted.com/member/3152867431"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-badge text-[9px] underline mt-1 inline-block"
                  style={{ color: V.accent }}
                  data-testid="vinted-reviews-link"
                >
                  {t("viewVintedReviews")} ↗
                </a>
              </div>
              <div><p className="font-['Space_Grotesk'] text-3xl font-bold" style={{ color: V.text }}>24–48h</p><p className={mono} style={{ color: V.muted }}>{t("dispatchTime")}</p></div>
            </Reveal>
          )}
        </div>
        <div className="px-6 sm:px-12 mb-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { icon: Truck, label: t("shipsIn") },
            { icon: Lock, label: t("secure") },
            { icon: Undo2, label: t("easyReturns") },
            { icon: PackageCheck, label: t("orderTracking") },
          ].map((b, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3.5" style={{ border: `1px solid ${V.border}`, background: V.surface }} data-testid={`trust-badge-${i}`}>
              <b.icon size={16} strokeWidth={1.5} style={{ color: V.accent }} />
              <span className="font-badge text-[9px]" style={{ color: V.text }}>{b.label}</span>
            </div>
          ))}
        </div>
        {reviews && (
          <Ticker duration={45} pauseOnHover testid="reviews-ticker">
            {reviews.items.map((rv, i) => (
              <div key={i} className="w-72 sm:w-80 h-52 flex flex-col mx-2 shrink-0 p-6" style={{ background: V.surface, border: `1px solid ${V.border}` }} data-testid={`review-card-${i}`}>
                <div className="flex gap-0.5 mb-3" data-testid={`review-stars-${i}`}>
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} size={13} strokeWidth={1.5} fill={s < rv.rating ? "currentColor" : "none"} style={{ color: s < rv.rating ? V.accent : V.border }} />
                  ))}
                </div>
                <p className="text-sm leading-relaxed flex-1 overflow-hidden" style={{ color: V.text }}>“{rv.text}”</p>
                <div className="flex justify-between items-center mt-4 gap-2">
                  <span className="font-badge text-[10px] truncate" style={{ color: V.muted }}>{rv.author}</span>
                  <span className="font-badge text-[8px] px-1.5 py-0.5 whitespace-nowrap" style={{ border: `1px solid ${V.border}`, color: V.muted }}>{t("verifiedBadge")}</span>
                </div>
              </div>
            ))}
          </Ticker>
        )}
      </section>

      {/* 6.5 — FAQ (admin-managed) */}
      {faqs.length > 0 && (
        <section id="faq" className="px-6 sm:px-12 py-16 sm:py-24" style={{ borderBottom: `1px solid ${V.border}` }} data-testid="faq-section">
          <Reveal className="mb-10">
            <p className={mono} style={{ color: V.accent }}>{t("faqKicker")}</p>
            <h2 className={`${h2cls} mt-3`}>{t("faqTitle")}</h2>
          </Reveal>
          <div className="max-w-3xl">
            {faqs.map((f, i) => {
              const fq = f[`q_${lang}`] || f.q;
              const fa = f[`a_${lang}`] || f.a;
              return (
              <div key={f.id} style={{ border: `1px solid ${V.border}`, marginTop: i > 0 ? "-1px" : 0, background: V.surface }} data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setFaqOpen(faqOpen === f.id ? null : f.id)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  data-testid={`faq-question-${i}`}
                >
                  <span className="font-['Space_Grotesk'] text-base font-bold uppercase tracking-wide" style={{ color: V.text }}>{fq}</span>
                  <span className="font-['Space_Grotesk'] text-xl shrink-0 transition-transform duration-200" style={{ color: V.accent, transform: faqOpen === f.id ? "rotate(45deg)" : "none" }}>+</span>
                </button>
                {faqOpen === f.id && (
                  <p className="px-6 pb-6 text-sm leading-relaxed whitespace-pre-line" style={{ color: V.muted }} data-testid={`faq-answer-${i}`}>{fa}</p>
                )}
              </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 7 — NEWSLETTER (forest fold) */}
      <section id="newsletter" className="px-6 sm:px-12 py-20 sm:py-28 text-center" style={{ background: V.accent }} data-testid="newsletter-section">
        <Reveal>
          <p className={mono} style={{ color: "rgba(255,255,255,0.75)" }}>{t("newsletterKicker")}</p>
          <h2 className="font-['Space_Grotesk'] text-4xl sm:text-6xl font-bold uppercase tracking-tighter mt-4 leading-[0.95] text-white" data-testid="newsletter-title">
            {t("getFirstAccess")}
          </h2>
          <p className="mt-5 text-sm max-w-md mx-auto" style={{ color: "rgba(255,255,255,0.75)" }}>{t("newsletterSub2")}</p>
          <NewsletterForm t={t} />
        </Reveal>
      </section>
    </div>
  );
}

function PieceCard({ p, t, i }) {
  const gone = p.status === "sold" || p.status === "reserved";
  const vorschau = p.drop_state === "vorschau";
  const { add } = useCart();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const imgRef = useRef(null);
  const sheenRef = useRef(null);
  const rafRef = useRef(0);
  const [hovered, setHovered] = useState(false);
  const touch = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

  // rAF-throttled direct-DOM 3D tilt — no per-frame React re-renders, butter smooth
  const onMove = (e) => {
    if (window.matchMedia("(pointer: coarse), (prefers-reduced-motion: reduce)").matches || rafRef.current) return;
    const cx = e.clientX;
    const cy = e.clientY;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const el = cardRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = (cx - r.left) / r.width - 0.5;
      const y = (cy - r.top) / r.height - 0.5;
      el.style.transition = "transform 0.08s linear, box-shadow 0.3s";
      el.style.transform = `perspective(1000px) rotateX(${(-y * 10).toFixed(2)}deg) rotateY(${(x * 13).toFixed(2)}deg) translateY(-6px)`;
      if (imgRef.current) imgRef.current.style.transform = `scale(1.09) translate(${(-x * 12).toFixed(1)}px, ${(-y * 9).toFixed(1)}px)`;
      if (sheenRef.current) {
        sheenRef.current.style.opacity = "1";
        sheenRef.current.style.background = `radial-gradient(circle at ${((x + 0.5) * 100).toFixed(1)}% ${((y + 0.5) * 100).toFixed(1)}%, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.1) 34%, transparent 64%)`;
      }
    });
  };
  const onLeave = () => {
    setHovered(false);
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    if (cardRef.current) {
      cardRef.current.style.transition = "transform 0.55s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s";
      cardRef.current.style.transform = "perspective(1000px)";
    }
    if (imgRef.current) imgRef.current.style.transform = "scale(1)";
    if (sheenRef.current) sheenRef.current.style.opacity = "0";
  };

  const quickAdd = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (gone) return;
    add(p); trackAddToCart(p); trackEvent("add_to_cart", { product_id: p.id || p._id });
    flyToCart(imgRef.current);
  };
  const quickBuy = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (gone) return;
    add(p); trackAddToCart(p); trackEvent("add_to_cart", { product_id: p.id || p._id });
    navigate("/cart");
  };

  return (
    <Reveal delay={(i % 4) * 0.06}>
      <Link
        to={`/item/${p.id || p._id}`}
        data-testid={`featured-card-${i}`}
        ref={cardRef}
        onMouseMove={onMove}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={onLeave}
        className="group block relative"
        style={{
          background: V.surface, border: `1px solid ${V.border}`,
          transform: "perspective(1000px)",
          transition: "transform 0.55s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s",
          transformStyle: "preserve-3d",
          boxShadow: hovered ? "0 30px 60px -22px rgba(20,24,20,0.45), 0 8px 20px -10px rgba(20,24,20,0.25)" : "0 1px 0 rgba(0,0,0,0.04)",
        }}
      >
        <div className="aspect-[3/4] overflow-hidden relative">
          <img
            ref={imgRef}
            src={p.images?.[0]}
            alt={p.name}
            loading="lazy"
            className="w-full h-full object-cover"
            style={{ transform: "scale(1)", transition: "transform 0.5s cubic-bezier(0.22,1,0.36,1)", filter: "saturate(1.05)" }}
          />
          {/* cursor-following pearl sheen (imperative, GPU-only) */}
          <div
            ref={sheenRef}
            className="absolute inset-0 pointer-events-none transition-opacity duration-300"
            style={{ opacity: 0, mixBlendMode: "soft-light" }}
            data-testid={`featured-sheen-${i}`}
          />
          {/* quick-purchase bar — slides up on hover, always visible on touch */}
          <div
            className="absolute bottom-0 left-0 right-0 z-10 flex items-center justify-between gap-2 px-3 py-2.5 transition-transform duration-300"
            style={{
              background: "rgba(250,248,243,0.96)", backdropFilter: "blur(8px)", borderTop: `1px solid ${V.border}`,
              transform: hovered || touch ? "translateY(0)" : "translateY(100%)",
            }}
            data-testid={`featured-quickbar-${i}`}
          >
            <span className="font-['Space_Grotesk'] text-sm font-extrabold" style={{ color: V.text }} data-testid={`featured-price-${i}`}>
              {formatPrice(p.price)}
            </span>
            {vorschau ? (
              <span className="font-badge text-[9px] shrink-0" style={{ color: V.muted }} data-testid={`featured-vorschau-${i}`}>{t("soonAvailable").toUpperCase()}</span>
            ) : (
            <span className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={quickAdd}
                disabled={gone}
                data-testid={`featured-add-${i}`}
                aria-label={t("addToCart")}
                className="inline-flex items-center justify-center w-11 h-11 transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed hover:text-white"
                style={{ border: `1px solid ${V.text}`, color: V.text, background: "transparent" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = V.text; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <ShoppingBag size={13} strokeWidth={1.75} />
              </button>
              <button
                type="button"
                onClick={quickBuy}
                disabled={gone}
                data-testid={`featured-buy-${i}`}
                className="inline-flex items-center gap-1.5 h-8 px-3 font-badge text-[9px] text-white transition-all duration-150 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: V.accent }}
              >
                {t("buyNow")} <ArrowRight size={11} strokeWidth={2} />
              </button>
            </span>
            )}
          </div>
        </div>
        {vorschau && !gone ? (
          <span className="absolute top-2.5 left-2.5 font-badge text-[8px] px-2 py-1 z-10" style={{ background: V.text, color: V.bg, transform: "translateZ(44px)" }} data-testid={`featured-soon-${i}`}>
            {t("soonAvailable").toUpperCase()}
          </span>
        ) : gone ? (
          <span className="absolute top-2.5 left-2.5 font-badge text-[8px] px-2 py-1 z-10" style={{ background: V.bg, color: V.muted, border: `1px solid ${V.border}`, transform: "translateZ(44px)" }} data-testid={`featured-sold-${i}`}>
            {t("sold").toUpperCase()}
          </span>
        ) : (
          <span className="absolute top-2.5 left-2.5 font-badge text-[8px] px-2 py-1 text-white z-10" style={{ background: V.accent, transform: "translateZ(44px)" }} data-testid={`featured-oneofone-${i}`}>
            1 OF 1
          </span>
        )}
        <div className="p-3.5" style={{ borderTop: `1px solid ${V.border}` }}>
          <p className="font-badge text-[8px] tracking-[0.2em]" style={{ color: V.muted }}>{p.brand}</p>
          <p className="font-['Space_Grotesk'] text-sm font-bold uppercase tracking-wide mt-1 leading-snug" style={{ color: V.text }}>
            {p.name}
          </p>
          <div className="flex items-center justify-between mt-2.5">
            <span className="font-badge text-[9px]" style={{ color: V.muted }}>
              {p.condition}{p.size && p.size !== "—" ? ` · ${p.size}` : ""}
            </span>
            <span className="font-['Space_Grotesk'] text-sm font-extrabold" style={{ color: V.accent }}>{formatPrice(p.price)}</span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
}

function NewsletterForm({ t }) {
  const [email, setEmail] = useState("");
  const subscribe = async () => {
    if (!email.trim()) return;
    try {
      const r = await api.post("/subscribe", { email });
      toast.success(r.data?.pending ? t("subscribedPending") : t("subscribed"));
      setEmail("");
    } catch {
      toast.error(t("contactErr"));
    }
  };
  return (
    <div className="mt-8 max-w-lg mx-auto">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          data-testid="newsletter-email-input"
              aria-label="E-Mail für Newsletter"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("subscribePh")}
          className="flex-1 px-5 py-4 text-sm focus:outline-none placeholder:text-[rgba(255,255,255,0.5)]"
          style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.35)", color: "#fff" }}
        />
        <button
          onClick={subscribe}
          data-testid="newsletter-subscribe-button"
          className="font-badge text-xs px-8 py-4 transition-opacity hover:opacity-90"
          style={{ background: "#10180F", color: "#fff" }}
        >
          {t("subscribeBtn")}
        </button>
      </div>
      <p className="mt-3 font-badge text-[9px] text-left" style={{ color: "rgba(255,255,255,0.55)" }} data-testid="newsletter-privacy-note">
        {t("subscribePrivacy")}
      </p>
    </div>
  );
}
